import Link from "next/link";
import {
  ArrowUpRight,
  BookOpen,
  CalendarDays,
  Clock,
  Film,
  MapPin,
  Mic,
  PlaySquare,
  Radio,
  type LucideIcon,
} from "lucide-react";
import { ImageFrame } from "@/components/ui/ImageFrame";
import { langPath, type Dict, type Lang } from "@/lib/i18n";
import { cn } from "@/components/ui/cn";
import { modulePoster } from "@/lib/lecturehub/poster";
import { formatDate, formatDuration, formatEpisodes, formatLessons } from "@/lib/format";
import { moduleHref } from "./moduleHref";
import type { Module } from "@/lib/lecturehub/types";
import { isFreeToUnlock } from "./paywall";

type RowModel = {
  href: string;
  openHref: string;
  poster: string | null;
  posterFallback: string;
  typeLabel: string;
  TypeIcon: LucideIcon;
  statusLabel?: string;
  archived?: boolean;
  live?: boolean;
  facts: { Icon?: LucideIcon; label: string }[];
};

function rowModelFor(
  lang: Lang,
  t: Dict,
  m: Module,
  enrolled: boolean,
  cfHash: string | null,
  library: boolean,
): RowModel {
  const units = t.units;
  const poster = modulePoster(m, cfHash);
  const href = moduleHref(lang, m, library);

  switch (m.refType) {
    case "event": {
      const ev = m.event;
      const live = ev?.eventType === "live";
      const location =
        ev?.city && ev?.country ? `${ev.city}, ${ev.country}` : (ev?.city ?? ev?.country ?? null);
      const facts: RowModel["facts"] = [];
      if (ev?.dateFrom) facts.push({ Icon: CalendarDays, label: formatDate(ev.dateFrom, lang) });
      if (location) facts.push({ Icon: MapPin, label: location });
      return {
        href,
        openHref: href,
        poster,
        posterFallback: t.detail.typeEvent,
        typeLabel: t.detail.typeEvent,
        TypeIcon: CalendarDays,
        statusLabel: isFreeToUnlock(m.price) && !live ? t.detail.free : undefined,
        live,
        facts,
      };
    }
    case "podcast": {
      const pod = m.podcast;
      const isSeries = pod?.audioType === "simple_series" || pod?.audioType === "section_series";
      const episodeCount = isSeries ? (pod?.audios?.length ?? 0) : 1;
      const totalDuration = isSeries
        ? (pod?.audios ?? []).reduce((s, a) => s + (a.audio?.duration ?? 0), 0)
        : (pod?.audio?.duration ?? null);
      const facts: RowModel["facts"] = [];
      if (isSeries && episodeCount > 0)
        facts.push({ Icon: Radio, label: formatEpisodes(episodeCount, lang, units) });
      if (totalDuration && totalDuration > 0)
        facts.push({ Icon: Clock, label: formatDuration(totalDuration) });
      return {
        href,
        openHref: href,
        poster,
        posterFallback: t.detail.typePodcast,
        typeLabel: t.detail.typePodcast,
        TypeIcon: Mic,
        statusLabel: isFreeToUnlock(m.price) ? t.detail.free : undefined,
        facts,
      };
    }
    case "ott": {
      const ott = m.ott;
      const duration = ott?.duration ?? null;
      const isSeries = ott?.videoType === "simple_series" || ott?.videoType === "section_series";
      const episodeCount = isSeries ? (ott?.videos?.length ?? 0) : null;
      const facts: RowModel["facts"] = [];
      if (episodeCount != null && episodeCount > 0)
        facts.push({ Icon: Film, label: formatEpisodes(episodeCount, lang, units) });
      if (duration && duration > 0) facts.push({ Icon: Clock, label: formatDuration(duration) });
      return {
        href,
        openHref: href,
        poster,
        posterFallback: t.detail.typeVideo,
        typeLabel: t.detail.typeVideo,
        TypeIcon: PlaySquare,
        statusLabel: isFreeToUnlock(m.price) ? t.detail.free : undefined,
        facts,
      };
    }
    case "course":
    default: {
      // Backend-computed; see CourseCard for why the curriculum walk went away.
      const lessonCount = m.lessonsTotalCount ?? null;
      const openHref = enrolled ? langPath(lang, `/learn/${m.id}/${m.slug ?? ""}`) : href;
      const facts: RowModel["facts"] = [];
      if (lessonCount != null)
        facts.push({ Icon: BookOpen, label: formatLessons(lessonCount, lang, units) });
      return {
        href,
        openHref,
        poster,
        posterFallback: t.detail.typeCourse,
        typeLabel: t.detail.typeCourse,
        TypeIcon: BookOpen,
        statusLabel: isFreeToUnlock(m.price) ? t.detail.free : undefined,
        facts,
      };
    }
  }
}

function ModuleRow({
  lang,
  t,
  module: m,
  enrolled = false,
  cfHash = null,
  library = false,
}: {
  lang: Lang;
  t: Dict;
  module: Module;
  enrolled?: boolean;
  cfHash?: string | null;
  library?: boolean;
}) {
  const model = rowModelFor(lang, t, m, enrolled, cfHash, library);
  const cta = enrolled
    ? t.detail.continueCta
    : m.refType === "course"
      ? t.detail.enroll
      : t.detail.exploreCta;

  return (
    <Link
      href={model.openHref}
      aria-label={m.name ?? undefined}
      className={cn(
        "module-row group/row relative flex items-stretch gap-3 md:gap-5 p-2.5 md:p-4 bg-card text-card-foreground rounded-card border border-border/60 transition-all duration-200 hover:no-underline hover:border-foreground/15 hover:-translate-y-[1px]",
        model.archived ? "opacity-75" : null,
      )}
    >
      <div className="module-row__poster relative shrink-0 w-28 sm:w-32 md:w-44 lg:w-52 overflow-hidden rounded-lg">
        <div className="transition-transform duration-500 ease-out group-hover/row:scale-[1.04]">
          <ImageFrame
            src={model.poster}
            alt={m.name ?? ""}
            aspect="16/9"
            className="!border-0"
            fallback={model.posterFallback}
            monogram={m.name ?? null}
          />
        </div>
        {model.live ? (
          <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-1.5 h-5 rounded-full text-[9px] font-bold uppercase tracking-[0.14em] bg-destructive text-destructive-foreground">
            ● {t.detail.live}
          </span>
        ) : null}
        {model.archived ? (
          <span className="absolute top-1.5 left-1.5 inline-flex items-center px-1.5 h-5 rounded-full text-[9px] font-bold uppercase tracking-[0.14em] bg-foreground/70 text-background">
            {t.listing.archive}
          </span>
        ) : null}
      </div>

      <div className="flex-1 min-w-0 flex flex-col gap-1 md:gap-1.5 py-0.5 md:py-1">
        <p className="text-[10px] md:text-[11px] uppercase tracking-[0.16em] font-bold text-muted-foreground inline-flex items-center gap-1.5 flex-wrap">
          <model.TypeIcon
            className="size-3.5 md:size-4 text-accent shrink-0"
            strokeWidth={2.25}
            aria-hidden
          />
          <span>{model.typeLabel}</span>
          {model.statusLabel ? (
            <>
              <span aria-hidden className="text-foreground/25">
                ·
              </span>
              <span>{model.statusLabel}</span>
            </>
          ) : null}
        </p>

        <h3 className="!font-semibold !text-[15px] md:!text-[18px] !leading-snug !text-foreground line-clamp-2 group-hover/row:text-primary transition-colors !mb-0">
          {m.name ?? "—"}
        </h3>

        {m.abstract ? (
          <p className="hidden md:block text-[13.5px] text-muted-foreground line-clamp-2 leading-snug">
            {m.abstract}
          </p>
        ) : null}

        {model.facts.length > 0 ? (
          <ul className="mt-auto pt-1 md:pt-2 flex items-center flex-wrap gap-x-2 gap-y-1 text-[11px] md:text-xs text-muted-foreground min-w-0">
            {model.facts.map((f, i) => (
              <li key={i} className="inline-flex items-center gap-1.5">
                {i > 0 ? (
                  <span aria-hidden className="text-foreground/25">
                    ·
                  </span>
                ) : null}
                {f.Icon ? <f.Icon className="size-3.5 shrink-0" aria-hidden /> : null}
                <span className="truncate">{f.label}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {/* Desktop CTA pill — hidden on mobile to keep the row compact;
          the whole row is the tap target there. */}
      <div className="hidden md:flex items-center pr-1">
        {enrolled ? (
          <span
            className="inline-flex items-center gap-1 shrink-0 text-[11px] uppercase tracking-[0.12em] font-bold rounded-full px-3 h-7 bg-accent text-accent-foreground transition-all duration-200 ease-out group-hover/row:brightness-105"
            style={{
              boxShadow:
                "0 1px 2px rgb(0 0 0 / 0.08), 0 4px 10px -4px color-mix(in srgb, color-mix(in srgb, var(--color-accent), black 45%) 30%, transparent), inset 0 1px 0 color-mix(in srgb, color-mix(in srgb, var(--color-accent), white 80%) 60%, transparent)",
            }}
          >
            {cta}
            <ArrowUpRight
              className="size-3 shrink-0 transition-transform duration-200 ease-out group-hover/row:translate-x-0.5"
              aria-hidden
            />
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 shrink-0 text-[11px] uppercase tracking-[0.12em] font-bold rounded-full px-3 h-7 bg-foreground/[0.06] text-foreground transition-all duration-200 ease-out group-hover/row:bg-accent group-hover/row:text-accent-foreground">
            {cta}
            <ArrowUpRight
              className="size-3 shrink-0 transition-transform duration-200 ease-out group-hover/row:translate-x-0.5"
              aria-hidden
            />
          </span>
        )}
      </div>

      <ArrowUpRight
        className="md:hidden absolute top-3 right-3 size-4 text-foreground/35 transition-all duration-200 group-hover/row:text-accent group-hover/row:translate-x-0.5"
        aria-hidden
      />
    </Link>
  );
}

// Shared server/client component — sync by design; the caller supplies the
// resolved Dict (server: await getDict(lang); client: useDict()).
export function ModuleList({
  modules,
  lang,
  t,
  emptyState,
  enrolledIds,
  cfHash = null,
  library = false,
}: {
  modules: Module[];
  lang: Lang;
  t: Dict;
  emptyState?: React.ReactNode;
  enrolledIds?: Set<string>;
  cfHash?: string | null;
  library?: boolean;
}) {
  if (modules.length === 0) {
    return (
      <div className="bg-muted/40 p-10 text-center text-muted-foreground rounded-2xl">
        {emptyState ?? t.listing.nothingToShow}
      </div>
    );
  }
  return (
    <ul className="flex flex-col gap-2.5 md:gap-3">
      {modules.map((m) => (
        <li key={m.id}>
          <ModuleRow
            module={m}
            lang={lang}
            t={t}
            enrolled={enrolledIds?.has(String(m.id)) ?? false}
            cfHash={cfHash}
            library={library}
          />
        </li>
      ))}
    </ul>
  );
}
